import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  LogOut, Wallet, BarChart3, Users,
  ChevronRight, DollarSign, MousePointer, Eye, Check, X,
  Link2, ExternalLink, Send, Clock, CheckCircle, AlertCircle, User,
  Bell, CreditCard, ArrowUpRight, Banknote, ArrowRightLeft
} from "lucide-react";
import AIHelpAssistant from "../components/AIHelpAssistant";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ProfileScoreCard from "../components/ProfileScoreCard";

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['dashboard', 'common', 'landing']);
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("available");
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [myCampaigns, setMyCampaigns] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [notification, setNotification] = useState(null);
  const [postUrlModal, setPostUrlModal] = useState(null);
  const [postUrl, setPostUrl] = useState("");
  const [payoutModal, setPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);

  // All API calls use httpOnly cookies (withCredentials: true)
  const axiosConfig = { withCredentials: true };

  const fetchData = useCallback(async () => {
    try {
      const [availableRes, myRes, walletRes, profileRes, notifRes, connectRes] = await Promise.all([
        axios.get(`${API}/creator/available-campaigns`, axiosConfig),
        axios.get(`${API}/creator/my-campaigns`, axiosConfig),
        axios.get(`${API}/creator/wallet`, axiosConfig),
        axios.get(`${API}/creator/profile`, axiosConfig),
        axios.get(`${API}/notifications`, axiosConfig),
        axios.get(`${API}/creator/connect/status`, axiosConfig).catch(() => ({ data: { has_account: false } }))
      ]);
      setAvailableCampaigns(availableRes.data);
      setMyCampaigns(myRes.data);
      setWallet(walletRes.data);
      setProfile(profileRes.data);
      setNotifications(notifRes.data);
      setConnectStatus(connectRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Check for connect return
    const connectStatus = searchParams.get("connect");
    if (connectStatus === "complete") {
      setNotification({ type: "success", message: "Payout account connected successfully!" });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (connectStatus === "refresh") {
      setNotification({ type: "warning", message: "Please complete your payout account setup." });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchData, searchParams]);

  const handleAcceptCampaign = async (campaignId) => {
    setActionLoading(campaignId);
    try {
      await axios.post(
        `${API}/creator/campaigns/${campaignId}/accept`,
        {},
        axiosConfig
      );
      setNotification({ type: "success", message: "Campaign accepted! Your tracking link has been generated." });
      fetchData();
    } catch (error) {
      setNotification({ 
        type: "error", 
        message: error.response?.data?.detail || "Failed to accept campaign" 
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineCampaign = async (campaignId) => {
    setActionLoading(campaignId);
    try {
      await axios.post(
        `${API}/creator/campaigns/${campaignId}/decline`,
        {},
        axiosConfig
      );
      setAvailableCampaigns(prev => prev.filter(c => c.campaign_id !== campaignId));
      setNotification({ type: "info", message: "Campaign declined" });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to decline campaign" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitPost = async () => {
    if (!postUrl.trim() || !postUrlModal) return;
    
    setActionLoading(postUrlModal.campaign_id);
    try {
      await axios.post(
        `${API}/creator/campaigns/${postUrlModal.campaign_id}/submit-post`,
        { post_url: postUrl },
        axiosConfig
      );
      setNotification({ type: "success", message: "Post URL submitted for verification!" });
      setPostUrlModal(null);
      setPostUrl("");
      fetchData();
    } catch (error) {
      setNotification({ type: "error", message: "Failed to submit post URL" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetupPayout = async () => {
    setActionLoading("setup-payout");
    try {
      const response = await axios.post(
        `${API}/creator/connect/create-account`,
        { origin_url: window.location.origin },
        axiosConfig
      );
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      if (error.response?.data?.detail?.includes("already exists")) {
        // Get new onboarding link
        const response = await axios.post(
          `${API}/creator/connect/onboarding-link`,
          { origin_url: window.location.origin },
          axiosConfig
        );
        window.location.href = response.data.onboarding_url;
      } else {
        setNotification({ type: "error", message: error.response?.data?.detail || "Failed to setup payout" });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) return;
    
    setActionLoading("payout");
    try {
      await axios.post(
        `${API}/creator/payout/request`,
        { amount: parseFloat(payoutAmount), payout_method: "stripe" },
        axiosConfig
      );
      setNotification({ type: "success", message: `€${payoutAmount} payout initiated!` });
      setPayoutModal(false);
      setPayoutAmount("");
      fetchData();
    } catch (error) {
      setNotification({ type: "error", message: error.response?.data?.detail || "Payout failed" });
    } finally {
      setActionLoading(null);
    }
  };

  const copyTrackingLink = (trackingCode) => {
    const link = `${window.location.origin}/t/${trackingCode}`;
    navigator.clipboard.writeText(link);
    setNotification({ type: "success", message: "Tracking link copied to clipboard!" });
  };

  const markNotificationRead = async (notifId) => {
    await axios.put(`${API}/notifications/${notifId}/read`, {}, axiosConfig);
    fetchData();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060D]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-tight">EleKtro Universal</span>
              <span className="text-[10px] text-gray-500 leading-tight">Conscious Creator</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Role Switcher - to Business */}
            <Link
              to="/"
              data-testid="switch-to-business-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm"
            >
              <ArrowRightLeft className="w-4 h-4" />
              {t('landing:nav.switchToBusiness')}
            </Link>
            
            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-btn"
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {notifications.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                    {notifications.unread_count}
                  </span>
                )}
              </button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 glass-card rounded-xl shadow-2xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-semibold text-white">{t('dashboard:business.notifications.title')}</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">{t('dashboard:business.notifications.empty')}</div>
                    ) : (
                      notifications.notifications.slice(0, 10).map((notif) => (
                        <div 
                          key={notif.notification_id}
                          onClick={() => markNotificationRead(notif.notification_id)}
                          className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 ${!notif.read ? 'bg-cyan-500/5' : ''}`}
                        >
                          <div className="font-medium text-white text-sm">{notif.title}</div>
                          <div className="text-xs text-gray-400 mt-1">{notif.message}</div>
                          <div className="text-xs text-gray-500 mt-2">
                            {new Date(notif.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={() => navigate("/creator/profile")}
              data-testid="profile-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
            >
              <User className="w-5 h-5" />
              {t('dashboard:creator.profile.title')}
            </button>
            <div className="text-right mr-4">
              <div className="text-sm text-gray-400">{t('dashboard:welcome', { name: '' }).replace(', ', '')}</div>
              <div className="font-semibold text-white">{user?.name}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              title={t('common:nav.logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            notification.type === "success" ? "bg-green-500/10 border border-green-500/30 text-green-400" :
            notification.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" :
            "bg-blue-500/10 border border-blue-500/30 text-blue-400"
          }`}>
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto hover:opacity-70">×</button>
          </div>
        )}

        {/* Profile Incomplete Banner */}
        {profile && (!profile.niches?.length || !profile.social_platforms?.length) && (
          <div className="mb-8 glass-card rounded-2xl p-6 border border-purple-500/30 bg-gradient-to-r from-purple-600/10 to-cyan-600/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-1">
                  {t('dashboard:creator.profile.complete')}
                </h3>
                <p className="text-gray-400">
                  {t('dashboard:creator.profile.subtitle')}
                </p>
              </div>
              <button
                onClick={() => navigate("/creator/profile")}
                className="btn-primary px-6 py-3 rounded-xl font-bold text-white"
              >
                {t('dashboard:creator.profile.complete')}
              </button>
            </div>
          </div>
        )}

        {/* Payout Setup Banner */}
        {!connectStatus?.payouts_enabled && wallet?.available_balance > 0 && (
          <div className="mb-8 glass-card rounded-2xl p-6 border border-cyan-500/30 bg-gradient-to-r from-cyan-600/10 to-blue-600/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-['Syne'] text-white mb-1">
                    {t('dashboard:creator.wallet.setupPayout')}
                  </h3>
                  <p className="text-gray-400">
                    {t('dashboard:creator.wallet.connectStripe')} - €{wallet.available_balance.toFixed(2)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleSetupPayout}
                disabled={actionLoading === "setup-payout"}
                data-testid="setup-payout-btn"
                className="btn-primary px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2"
              >
                {actionLoading === "setup-payout" ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <CreditCard className="w-5 h-5" />
                )}
                {t('dashboard:creator.wallet.setupPayout')}
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-['Unbounded'] text-white mb-2">
            {t('dashboard:creator.title')}
          </h1>
          <p className="text-gray-400 text-sm md:text-base">
            {t('dashboard:creator.subtitle')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { 
              label: t('dashboard:creator.stats.totalEarned'), 
              value: `€${wallet?.total_earnings?.toFixed(2) || "0.00"}`, 
              icon: DollarSign,
              color: "cyan" 
            },
            { 
              label: t('dashboard:creator.wallet.available'), 
              value: `€${wallet?.available_balance?.toFixed(2) || "0.00"}`, 
              icon: Wallet,
              color: "green" 
            },
            { 
              label: t('dashboard:creator.stats.pendingEarnings'), 
              value: `€${wallet?.pending_earnings?.toFixed(2) || "0.00"}`, 
              icon: Clock,
              color: "blue" 
            },
            { 
              label: t('dashboard:creator.stats.activeCampaigns'), 
              value: wallet?.total_campaigns || 0, 
              icon: BarChart3,
              color: "purple" 
            }
          ].map((stat, index) => (
            <div key={`stat-${index}`} className="glass-card rounded-xl p-5 card-hover">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                stat.color === "cyan" ? "bg-cyan-500/20" :
                stat.color === "green" ? "bg-green-500/20" :
                stat.color === "blue" ? "bg-blue-500/20" : "bg-purple-500/20"
              }`}>
                <stat.icon className={`w-5 h-5 ${
                  stat.color === "cyan" ? "text-cyan-400" :
                  stat.color === "green" ? "text-green-400" :
                  stat.color === "blue" ? "text-blue-400" : "text-purple-400"
                }`} />
              </div>
              <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Profile Completion Card - Transparent & Actionable */}
        <div className="mb-8">
          <ProfileScoreCard profile={profile} connectStatus={connectStatus} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-xl inline-flex">
          {[
            { id: "available", label: t('dashboard:creator.tabs.available'), count: availableCampaigns.length },
            { id: "active", label: t('dashboard:creator.tabs.myCampaigns'), count: myCampaigns.length },
            { id: "wallet", label: t('dashboard:creator.tabs.wallet'), count: null }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? "bg-white/10 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? "bg-cyan-500/20 text-cyan-400" : "bg-white/10"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Available Campaigns Tab */}
        {activeTab === "available" && (
          <div className="space-y-4">
            {availableCampaigns.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('dashboard:creator.campaigns.emptyAvailable')}</h3>
                <p className="text-gray-400">
                  {t('dashboard:creator.campaigns.emptyAvailableDesc')}
                </p>
              </div>
            ) : (
              availableCampaigns.map((campaign) => {
                // Calculate creator earnings (80% of reward pool)
                const creatorEarnings = campaign.creator_reward_pool > 0 
                  ? (campaign.creator_reward_pool * 0.8).toFixed(0) 
                  : 0;
                const goalLabel = campaign.goal === "customers" ? t('dashboard:goals.customers') 
                  : campaign.goal === "leads" ? t('dashboard:goals.leads') 
                  : t('dashboard:goals.traffic');
                
                return (
                  <div key={campaign.campaign_id} className="glass-card rounded-xl p-6 card-hover">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-white text-lg">{campaign.title}</h3>
                          {campaign.creator_reward_pool > 0 && (
                            <span className="badge-success px-3 py-1 rounded-full text-xs font-medium">
                              {t('dashboard:creator.campaigns.earnUpTo')} €{creatorEarnings}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 mb-3 line-clamp-2">{campaign.description}</p>
                        
                        {/* Payout clarity */}
                        {campaign.cost_per_result > 0 && (
                          <div className="mb-3 text-sm">
                            <span className="text-cyan-400">€{(campaign.cost_per_result * 0.8).toFixed(0)} {t('dashboard:creator.campaigns.perResult')}</span>
                            <span className="text-gray-500"> · {goalLabel}</span>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {campaign.target_niches?.slice(0, 3).map((niche, i) => (
                            <span key={i} className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                              {niche}
                            </span>
                          ))}
                          {campaign.target_platforms?.slice(0, 2).map((platform, i) => (
                            <span key={i} className="px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                              {platform}
                            </span>
                          ))}
                        </div>
                        <div className="text-sm text-gray-500">
                          {campaign.business_name}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleDeclineCampaign(campaign.campaign_id)}
                            disabled={actionLoading === campaign.campaign_id}
                            data-testid={`decline-${campaign.campaign_id}`}
                            className="px-5 py-2.5 rounded-xl border border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-all"
                            title={t('dashboard:creator.campaigns.decline')}
                          >
                            <X className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleAcceptCampaign(campaign.campaign_id)}
                            disabled={actionLoading === campaign.campaign_id}
                            data-testid={`accept-${campaign.campaign_id}`}
                            className="btn-primary px-6 py-2.5 rounded-xl font-semibold text-white flex items-center gap-2 disabled:opacity-50"
                          >
                            {actionLoading === campaign.campaign_id ? (
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                              <>
                                <Check className="w-5 h-5" />
                                {t('dashboard:creator.campaigns.accept')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* My Campaigns Tab */}
        {activeTab === "active" && (
          <div className="space-y-4">
            {myCampaigns.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('dashboard:creator.campaigns.emptyMy')}</h3>
                <p className="text-gray-400">
                  {t('dashboard:creator.campaigns.emptyMyDesc')}
                </p>
              </div>
            ) : (
              myCampaigns.map((participation) => {
                // Dynamic metric label based on campaign goal
                const goalMetricLabel = participation.campaign?.goal === "customers" ? "customers" 
                  : participation.campaign?.goal === "leads" ? "leads" 
                  : "clicks";
                const goalMetricValue = participation.campaign?.goal === "customers" ? (participation.conversions || 0)
                  : participation.campaign?.goal === "leads" ? (participation.leads || 0)
                  : (participation.clicks || 0);
                
                return (
                  <div key={participation.participation_id} className="glass-card rounded-xl p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-white text-lg">
                            {participation.campaign?.title}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            participation.post_verified ? "badge-success" :
                            participation.post_url ? "badge-warning" : "badge-info"
                          }`}>
                            {participation.post_verified ? "Verified" :
                             participation.post_url ? "Pending Review" : "Active"}
                          </span>
                          {participation.campaign?.goal && (
                            <span className="px-2 py-0.5 rounded text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                              {participation.campaign.goal === "customers" ? "Per Customer" 
                                : participation.campaign.goal === "leads" ? "Per Lead" 
                                : "Per Click"}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 mb-4 line-clamp-2">
                          {participation.campaign?.description}
                        </p>
                        
                        {/* Tracking Link */}
                        <div className="bg-white/5 rounded-xl p-4 mb-4">
                          <div className="text-sm text-gray-400 mb-2">Your Tracking Link</div>
                          <div className="flex items-center gap-3">
                            <code className="flex-1 bg-black/30 px-4 py-2 rounded-lg text-cyan-400 text-sm overflow-hidden text-ellipsis">
                              {window.location.origin}/t/{participation.tracking_code}
                            </code>
                            <button
                              onClick={() => copyTrackingLink(participation.tracking_code)}
                              data-testid={`copy-link-${participation.campaign_id}`}
                              className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
                            >
                              <Link2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Stats - Dynamic based on goal */}
                        <div className="flex gap-6 text-sm">
                          <span className="flex items-center gap-2 text-gray-400">
                            <MousePointer className="w-4 h-4" />
                            {goalMetricValue} {goalMetricLabel}
                          </span>
                          <span className="flex items-center gap-2 text-gray-400">
                            <Eye className="w-4 h-4" />
                            {participation.views} views
                          </span>
                          {participation.earned_amount > 0 && (
                            <span className="flex items-center gap-2 text-cyan-400">
                              <DollarSign className="w-4 h-4" />
                              €{participation.earned_amount.toFixed(2)} earned
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {!participation.post_url ? (
                          <button
                            onClick={() => setPostUrlModal(participation)}
                            data-testid={`submit-post-${participation.campaign_id}`}
                            className="btn-primary px-5 py-2.5 rounded-xl font-semibold text-white flex items-center gap-2"
                          >
                            <Send className="w-4 h-4" />
                            Submit Post
                          </button>
                        ) : (
                          <a
                            href={participation.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-5 py-2.5 rounded-xl font-semibold border border-white/20 text-white flex items-center gap-2 hover:bg-white/10 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View Post
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Wallet Tab */}
        {activeTab === "wallet" && (
          <div className="space-y-6">
            {/* Balance Card */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                  <Wallet className="w-8 h-8 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-400">{t('dashboard:creator.wallet.available')}</div>
                  <div className="text-4xl font-bold font-['Unbounded'] text-white">
                    €{wallet?.available_balance?.toFixed(2) || "0.00"}
                  </div>
                </div>
                {connectStatus?.payouts_enabled && wallet?.available_balance >= 10 && (
                  <button
                    onClick={() => setPayoutModal(true)}
                    data-testid="request-payout-btn"
                    className="btn-primary px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                    {t('dashboard:creator.wallet.withdraw')}
                  </button>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">{t('dashboard:creator.wallet.total')}</div>
                  <div className="text-2xl font-bold text-cyan-400">
                    €{wallet?.total_earnings?.toFixed(2) || "0.00"}
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">{t('dashboard:creator.wallet.pending')}</div>
                  <div className="text-2xl font-bold text-yellow-400">
                    €{wallet?.pending_earnings?.toFixed(2) || "0.00"}
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">{t('dashboard:creator.wallet.payoutStatus', 'Payout Status')}</div>
                  <div className={`text-lg font-bold ${connectStatus?.payouts_enabled ? 'text-green-400' : 'text-gray-400'}`}>
                    {connectStatus?.payouts_enabled ? t('dashboard:creator.wallet.payoutConnected') : t('dashboard:creator.wallet.payoutNotSetUp', 'Not Set Up')}
                  </div>
                </div>
              </div>

              {/* Setup Payout Button */}
              {!connectStatus?.payouts_enabled && (
                <button
                  onClick={handleSetupPayout}
                  disabled={actionLoading === "setup-payout"}
                  className="w-full py-4 rounded-xl border-2 border-dashed border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 transition-colors flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  {t('dashboard:creator.wallet.connectStripe')}
                </button>
              )}
            </div>

            {/* Earnings History */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4">{t('dashboard:creator.wallet.earningsHistory', 'Earnings History')}</h3>
              {wallet?.earnings_history?.length > 0 ? (
                <div className="space-y-3">
                  {wallet.earnings_history.map((entry) => (
                    <div key={entry.earning_id || `${entry.source}-${entry.campaign_id || entry.month}-${entry.created_at}`} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div>
                        <div className="font-medium text-white">
                          {entry.source === "monthly_reward_pool" ? t('dashboard:creator.wallet.monthlyPool', 'Monthly Pool') : t('dashboard:creator.wallet.campaignReward', 'Campaign Reward')}
                        </div>
                        <div className="text-sm text-gray-400">
                          {entry.source === "campaign_reward" ? `${t('dashboard:creator.wallet.campaign', 'Campaign')} #${entry.campaign_id?.slice(-8)}` : entry.month}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-cyan-400">+€{entry.amount.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">{entry.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('dashboard:creator.wallet.noEarnings', 'No earnings yet. Start promoting campaigns to earn!')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Post URL Modal */}
      {postUrlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="glass-card rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
              Submit Your Post URL
            </h3>
            <p className="text-gray-400 mb-4">
              Enter the URL of your social media post promoting this campaign for verification.
            </p>
            <input
              type="url"
              value={postUrl}
              onChange={(e) => setPostUrl(e.target.value)}
              placeholder="https://instagram.com/p/..."
              data-testid="post-url-input"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setPostUrlModal(null);
                  setPostUrl("");
                }}
                className="flex-1 px-4 py-3 rounded-xl border border-white/20 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPost}
                disabled={!postUrl.trim() || actionLoading === postUrlModal.campaign_id}
                data-testid="submit-post-url-btn"
                className="flex-1 btn-primary px-4 py-3 rounded-xl font-semibold text-white disabled:opacity-50"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout Modal */}
      {payoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="glass-card rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
              Request Payout
            </h3>
            <p className="text-gray-400 mb-2">
              Available balance: <span className="text-cyan-400 font-semibold">€{wallet?.available_balance?.toFixed(2)}</span>
            </p>
            <p className="text-gray-500 text-sm mb-4">Minimum payout: €10</p>
            <div className="relative mb-4">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
                min={10}
                max={wallet?.available_balance}
                data-testid="payout-amount-input"
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setPayoutModal(false);
                  setPayoutAmount("");
                }}
                className="flex-1 px-4 py-3 rounded-xl border border-white/20 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestPayout}
                disabled={!payoutAmount || parseFloat(payoutAmount) < 10 || parseFloat(payoutAmount) > wallet?.available_balance || actionLoading === "payout"}
                data-testid="confirm-payout-btn"
                className="flex-1 btn-primary px-4 py-3 rounded-xl font-semibold text-white disabled:opacity-50"
              >
                {actionLoading === "payout" ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
                ) : (
                  "Withdraw"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Help Assistant */}
      <AIHelpAssistant />
    </div>
  );
};

export default CreatorDashboard;
